import React from "react";
import {
  LogIn,
  LayoutDashboard,
  Building2,
  Contact2,
  Megaphone,
  FileText,
  Mail,
  Layers,
  ShieldCheck,
  Phone,
  MessageSquare,
} from "lucide-react";
import { Header } from "../../../components/header";
import { Card, ModernCardHeader } from "../../../components/cards";

const SECTIONS = [
  { id: "connexion", label: "Connexion" },
  { id: "tableau-de-bord", label: "Tableau de bord" },
  { id: "clients", label: "Fiches client" },
  { id: "contacts", label: "Contacts" },
  { id: "prospection", label: "Prospection" },
  { id: "suivi", label: "Rapports & articles" },
  { id: "emails", label: "Emails" },
  { id: "cms", label: "CMS & catégories" },
  { id: "roles", label: "Rôles & accès" },
];

const StatusBadge: React.FC<{ variant: "prospect" | "converti" | "auto"; children: React.ReactNode }> = ({
  variant,
  children,
}) => {
  const styles = {
    prospect: "bg-amber-100 text-amber-700",
    converti: "bg-green-100 text-green-700",
    auto: "bg-indigo-100 text-indigo-700",
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${styles[variant]}`}>
      {children}
    </span>
  );
};

const Field: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="border border-gray-200 dark:border-zinc-800 rounded-md px-3 py-2 bg-white dark:bg-surface-dark-alt">
    <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-zinc-400 mb-0.5">
      {label}
    </p>
    <p className="text-sm font-mono text-gray-800 dark:text-zinc-200">{value}</p>
  </div>
);

const Crumbs: React.FC<{ path: string[] }> = ({ path }) => (
  <div className="flex flex-wrap items-center gap-1.5 font-mono text-sm mb-3">
    {path.map((crumb, i) => (
      <React.Fragment key={crumb}>
        {i > 0 && <span className="text-gray-400 dark:text-zinc-600">→</span>}
        <span className="bg-primary-50 dark:bg-zinc-800 text-primary-700 dark:text-zinc-200 px-2 py-0.5 rounded">
          {crumb}
        </span>
      </React.Fragment>
    ))}
  </div>
);

const Callout: React.FC<{ variant?: "info" | "warn"; children: React.ReactNode }> = ({
  variant = "info",
  children,
}) => (
  <div
    className={`border-l-4 rounded-r-md px-4 py-3 my-4 text-sm ${
      variant === "warn"
        ? "border-amber-500 bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-200"
        : "border-primary-500 bg-primary-50 dark:bg-zinc-800 text-gray-800 dark:text-zinc-200"
    }`}
  >
    {children}
  </div>
);

const StepSection: React.FC<{
  id: string;
  num: string;
  icon: React.ReactNode;
  title: string;
  role: string;
  children: React.ReactNode;
}> = ({ id, num, icon, title, role, children }) => (
  <section id={id} className="scroll-mt-24 py-8 border-b border-gray-200 dark:border-zinc-800 last:border-b-0">
    <div className="flex items-center gap-3 mb-1">
      <div className="bg-primary-50 dark:bg-zinc-800 text-primary-600 dark:text-zinc-200 p-2 rounded-lg flex-shrink-0">
        {icon}
      </div>
      <span className="font-mono text-sm text-primary-600 dark:text-zinc-400">{num}</span>
      <h2 className="text-xl font-bold text-gray-900 dark:text-zinc-100">{title}</h2>
    </div>
    <p className="text-xs text-gray-500 dark:text-zinc-500 mb-4 ml-[52px]">{role}</p>
    <div className="ml-[52px] text-[15px] leading-relaxed text-gray-700 dark:text-zinc-300 space-y-3 max-w-2xl">
      {children}
    </div>
  </section>
);

export const Tuto: React.FC = () => {
  return (
    <>
      <Header title="Tuto" subtitle="Prise en main du CRM Scalance" />

      <div className="main-container">
        <div className="flex gap-8 items-start flex-col lg:flex-row">
          <nav className="lg:sticky lg:top-4 lg:w-56 flex-shrink-0 order-2 lg:order-1">
            <Card padding="sm">
              <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-zinc-400 font-semibold mb-2 px-1">
                Au fil de l'écran
              </p>
              <ul className="space-y-0.5">
                {SECTIONS.map((s, i) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-gray-600 dark:text-zinc-400 hover:bg-gray-50 dark:hover:bg-zinc-800 hover:text-primary-700 dark:hover:text-zinc-100"
                    >
                      <span className="font-mono text-xs text-gray-400 dark:text-zinc-600">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      {s.label}
                    </a>
                  </li>
                ))}
              </ul>
            </Card>
          </nav>

          <div className="flex-1 min-w-0 order-1 lg:order-2">
            <Card padding="lg">
              <p className="font-serif text-lg leading-relaxed text-gray-700 dark:text-zinc-300 mb-2 max-w-2xl">
                Scalance centralise le suivi d'une agence web&nbsp;: chaque prospect devient une fiche,
                chaque relance est tracée, chaque rapport SEO et chaque article rejoint l'historique du
                client. Ce guide suit l'ordre naturel d'une journée de travail — de la connexion à
                l'envoi d'un email.
              </p>
            </Card>

            <Card padding="lg" className="mt-4">
              <StepSection
                id="connexion"
                num="01"
                icon={<LogIn size={18} />}
                title="Se connecter"
                role="Tout le monde"
              >
                <p>
                  Sur l'écran de connexion, saisissez votre email et votre mot de passe professionnels
                  puis validez. Une fois connecté, le menu de gauche s'adapte automatiquement à votre
                  rôle (Administrateur, Employé ou Client) — vous ne verrez que ce qui vous concerne.
                </p>
                <Callout>
                  <strong>Pas de session partagée.</strong> La déconnexion se fait depuis le menu du
                  compte&nbsp;; pensez à vous déconnecter sur un poste commun.
                </Callout>
              </StepSection>

              <StepSection
                id="tableau-de-bord"
                num="02"
                icon={<LayoutDashboard size={18} />}
                title="Le tableau de bord"
                role="Tout le monde — contenu variable selon le rôle"
              >
                <p>
                  C'est l'écran d'accueil, accessible à tout moment via <code>Dashboard</code> dans le
                  menu. Il affiche en un coup d'œil le nombre de clients enregistrés, les derniers
                  clients ajoutés, et des raccourcis vers les Prospections, les Rapports SEO et les
                  Articles.
                </p>
                <p>
                  Pensez-y comme un point de départ, pas une destination&nbsp;: chaque bloc renvoie vers
                  la liste complète correspondante.
                </p>
              </StepSection>

              <StepSection
                id="clients"
                num="03"
                icon={<Building2 size={18} />}
                title="Créer et suivre une fiche client"
                role="Cœur du CRM"
              >
                <p>
                  Chaque prospect ou client possède une fiche unique, accessible depuis{" "}
                  <code>Clients</code> dans le menu. C'est le point d'entrée de tout le reste&nbsp;:
                  contacts, prospections, rapports et articles s'y rattachent.
                </p>

                <div className="border border-gray-200 dark:border-zinc-800 rounded-lg p-4 bg-gray-50 dark:bg-zinc-900">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-zinc-400 font-semibold mb-2">
                    Créer une fiche
                  </p>
                  <Crumbs path={["Clients", "Nouveau client"]} />
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <Field label="Nom" value="obligatoire" />
                    <Field label="Email / Téléphone" value="optionnels" />
                    <Field label="Site" value="URL du site web" />
                    <Field label="Trouvé via" value="Google / Pages Jaunes / Autre" />
                    <Field label="CMS" value="recherche ou création" />
                    <Field label="Catégorie" value="secteur d'activité" />
                  </div>
                </div>

                <p>
                  Seul le <strong>nom</strong> est requis — tout le reste peut être complété plus tard,
                  au fil des échanges avec le prospect.
                </p>

                <p>
                  <strong>Le statut d'une fiche</strong> se voit d'un coup d'œil&nbsp;:{" "}
                  <StatusBadge variant="prospect">Prospect</StatusBadge> — pas encore client, en cours
                  de démarchage. <StatusBadge variant="converti">Converti</StatusBadge> — le prospect
                  est devenu client.
                </p>
                <p>
                  Changez ce statut depuis la fiche dès qu'un prospect signe&nbsp;: cela met à jour tous
                  les filtres et listes qui en dépendent.
                </p>

                <Callout>
                  <strong>Le champ CMS</strong> fonctionne en recherche-ou-création&nbsp;: tapez le nom
                  d'un CMS existant (WordPress, Shopify…) pour le sélectionner, ou un nom inédit pour le
                  créer à la volée — pas besoin de le créer à part avant.
                </Callout>

                <p>
                  Une fois la fiche ouverte, elle regroupe des onglets pour tout ce qui touche ce
                  client&nbsp;: Contacts, Prospections, Emails, Rapports SEO et Articles. Chaque onglet a
                  son propre formulaire d'ajout rapide directement en dessous de sa liste.
                </p>
              </StepSection>

              <StepSection
                id="contacts"
                num="04"
                icon={<Contact2 size={18} />}
                title="Gérer les contacts"
                role="Employés & administrateurs"
              >
                <p>
                  Un contact est une personne physique rattachée à un client&nbsp;: interlocuteur,
                  décideur, responsable technique. Ajoutez-en un depuis l'onglet <code>Contacts</code>{" "}
                  de la fiche client, ou depuis le menu <code>Contacts</code> pour une vue globale et
                  filtrable.
                </p>
                <p>
                  Marquez un contact comme <strong>principal</strong> quand plusieurs personnes sont
                  enregistrées pour un même client&nbsp;: il apparaîtra en priorité dans les listes.
                </p>
              </StepSection>

              <StepSection
                id="prospection"
                num="05"
                icon={<Megaphone size={18} />}
                title="Enregistrer une prospection"
                role="Commerciaux & agents automatiques"
              >
                <p>
                  Chaque tentative de démarchage — appel, email ou SMS — se note comme une{" "}
                  <strong>prospection</strong>, avec le message envoyé. C'est l'historique qui prouve
                  qu'on a relancé un prospect, et quand.
                </p>

                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1.5 font-mono text-xs border border-gray-200 dark:border-zinc-700 rounded-full px-3 py-1.5">
                    <Phone size={13} /> Téléphone — numéro + résumé
                  </span>
                  <span className="inline-flex items-center gap-1.5 font-mono text-xs border border-gray-200 dark:border-zinc-700 rounded-full px-3 py-1.5">
                    <Mail size={13} /> Email — adresse + contenu
                  </span>
                  <span className="inline-flex items-center gap-1.5 font-mono text-xs border border-gray-200 dark:border-zinc-700 rounded-full px-3 py-1.5">
                    <MessageSquare size={13} /> SMS — numéro + texte
                  </span>
                </div>

                <p>
                  La case <StatusBadge variant="auto">Auto</StatusBadge> signale une relance envoyée
                  automatiquement plutôt que manuellement par un commercial — utile pour distinguer le
                  travail humain des séquences automatisées.
                </p>
                <p>
                  Depuis une fiche client, le champ client est déjà pré-rempli&nbsp;; depuis le menu{" "}
                  <code>Prospections</code>, vous pouvez enregistrer une relance même pour un contact
                  pas encore fiché comme client.
                </p>
              </StepSection>

              <StepSection
                id="suivi"
                num="06"
                icon={<FileText size={18} />}
                title="Rapports SEO et articles"
                role="Équipe SEO & rédaction"
              >
                <p>Deux onglets de la fiche client suivent le travail de fond mené pour un site&nbsp;:</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-zinc-800">
                        <th className="text-left py-2 pr-4 text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                          Onglet
                        </th>
                        <th className="text-left py-2 pr-4 text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                          À quoi il sert
                        </th>
                        <th className="text-left py-2 text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                          Rythme
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100 dark:border-zinc-800">
                        <td className="py-2 pr-4 font-medium">Rapports SEO</td>
                        <td className="py-2 pr-4">
                          Compte-rendu texte libre des actions menées sur le site du client
                        </td>
                        <td className="py-2">Hebdomadaire</td>
                      </tr>
                      <tr>
                        <td className="py-2 pr-4 font-medium">Articles</td>
                        <td className="py-2 pr-4">
                          Contenu rédigé pour le site du client — titre, type et texte complet
                        </td>
                        <td className="py-2">Au fil de la production</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p>
                  Pour un article, précisez s'il s'agit d'un format <strong>Long</strong> ou{" "}
                  <strong>Court</strong>&nbsp;: cela permet de filtrer la liste par la suite. Les deux
                  onglets possèdent aussi une vue globale (<code>Rapports SEO</code> et{" "}
                  <code>Articles</code> dans le menu) pour consulter tous les clients à la fois.
                </p>
              </StepSection>

              <StepSection
                id="emails"
                num="07"
                icon={<Mail size={18} />}
                title="Connecter sa boite mail et échanger"
                role="Chaque utilisateur, individuellement"
              >
                <p>
                  Depuis <code>Ma boite mail</code>, connectez votre adresse professionnelle
                  (IMAP/SMTP) une seule fois. Les emails reçus sont automatiquement rattachés à la fiche
                  client correspondante lorsque l'adresse correspond.
                </p>
                <Callout variant="warn">
                  <strong>La synchronisation n'est pas instantanée.</strong> Les emails reçus mettent
                  quelques minutes à apparaître — actualisez l'onglet <code>Emails</code> de la fiche
                  client plutôt que d'attendre un rafraîchissement automatique.
                </Callout>
                <p>
                  Pour envoyer un email à un client, utilisez le formulaire de l'onglet{" "}
                  <code>Emails</code> de sa fiche&nbsp;: l'adresse est pré-remplie si elle est connue,
                  et l'envoi part immédiatement de votre boite connectée.
                </p>
              </StepSection>

              <StepSection
                id="cms"
                num="08"
                icon={<Layers size={18} />}
                title="CMS et catégories"
                role="Référentiels partagés"
              >
                <p>
                  <strong>CMS</strong> (menu <code>CMS</code>) liste les technologies de site utilisées
                  par vos clients — WordPress, Shopify, etc. Vous n'avez presque jamais besoin d'y aller
                  directement, puisque le champ CMS d'une fiche client en crée un nouveau
                  automatiquement si besoin. Cette page sert surtout à renommer ou fusionner des
                  doublons.
                </p>
                <p>
                  <strong>Catégories</strong> (menu <code>Categories</code>, réservé aux administrateurs
                  et employés) classe les clients par secteur d'activité — plombier, électricien,
                  restaurant… Utile pour filtrer et segmenter vos listes de prospection.
                </p>
              </StepSection>

              <StepSection
                id="roles"
                num="09"
                icon={<ShieldCheck size={18} />}
                title="Qui voit quoi"
                role="Bon à savoir"
              >
                <div className="flex flex-wrap gap-2">
                  <span className="font-mono text-xs border border-gray-200 dark:border-zinc-700 rounded-full px-3 py-1.5">
                    <strong>Administrateur</strong> — accès complet, gestion des utilisateurs et
                    catégories, peut supprimer
                  </span>
                  <span className="font-mono text-xs border border-gray-200 dark:border-zinc-700 rounded-full px-3 py-1.5">
                    <strong>Employé</strong> — accès complet aux clients et à la prospection, sans
                    gestion des comptes
                  </span>
                  <span className="font-mono text-xs border border-gray-200 dark:border-zinc-700 rounded-full px-3 py-1.5">
                    <strong>Client</strong> — accès limité à ses propres échanges
                  </span>
                </div>
                <p>
                  Si un bouton ou un onglet semble manquant, c'est probablement une question de rôle
                  plutôt qu'un bug&nbsp;: vérifiez auprès d'un administrateur si l'accès vous semble
                  nécessaire.
                </p>
              </StepSection>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};
